import type { InFieldUser } from '@cognite/apm-client';
import type { DirectRelationReference, ViewDefinition } from '@cognite/sdk';

import type {
  InFieldCDMLocationConfig,
  ViewMappingReference,
} from '../../features/app-config/cdm-types';
import {
  getRequiredAssetViewReferences,
  isViewReferenceMatch,
} from '../fdm-services/cdm-view-reference-utils';

import type { OfflineDataStoreScope } from './offline-data-store';

export const getCdmConfigurationQueryKey = (scope: OfflineDataStoreScope) => [
  'cdm-offline-configuration',
  scope,
];

/** Persisted values are JSON, including dates converted to strings. */
export interface CdmOfflineConfiguration {
  user: Pick<InFieldUser, 'externalId' | 'preferences'>;
  groups: string[];
  locations: InFieldCDMLocationConfig[];
  assetViewReferences: ViewMappingReference[];
  assetViews: ViewDefinition[];
}

const isSameInstance = (
  a: Partial<DirectRelationReference>,
  b: Partial<DirectRelationReference>
) => a.space === b.space && a.externalId === b.externalId;

/**
 * Returns the configuration if it belongs to the scope's user and root location and holds
 * the asset views CDM startup needs. Throws otherwise.
 */
export const assertCdmOfflineConfiguration = (
  configuration: CdmOfflineConfiguration,
  scope: OfflineDataStoreScope
): CdmOfflineConfiguration => {
  const { user, locations, assetViewReferences, assetViews } = configuration;
  if (user.externalId !== scope.userId) throw new Error('Wrong user');
  const preferredLocation = user.preferences?.infield?.location;
  const preferredLocationConfig = locations.find(
    (location) =>
      preferredLocation && isSameInstance(location, preferredLocation)
  );
  if (!preferredLocationConfig)
    throw new Error('Preferred location is missing');
  // The location config and its root location are different instances, so their IDs can differ.
  const root = preferredLocationConfig.dataStorage?.rootLocation;
  if (
    !root ||
    !isSameInstance(root, {
      externalId: scope.location,
      space: scope.locationSpace,
    })
  ) {
    throw new Error('Wrong root location');
  }
  const required = getRequiredAssetViewReferences(
    preferredLocationConfig.viewMappings?.asset
  );
  const hasReferences =
    assetViewReferences.length === required.length &&
    required.every((reference, i) =>
      isViewReferenceMatch(reference, assetViewReferences[i])
    );
  const hasViews = required.every((reference) =>
    assetViews.some((view) => isViewReferenceMatch(reference, view))
  );
  if (!hasReferences || !hasViews)
    throw new Error('Required views are missing');
  return configuration;
};
